import { AuthEnvelope, AuthSession } from '../types';
import { tlog } from '../v2/telemetry';

const API_BASE = '/api';
const LOCAL_STORAGE_KEY = 'leitura_v2_session';
const SESSION_STORAGE_KEY = 'leitura_v2_session_temp';

export interface StoredAuthSession {
  session: AuthSession;
  persistent: boolean;
}

function safeParseSession(raw: string | null, persistent: boolean): StoredAuthSession | null {
  if (!raw) return null;
  try {
    const session = JSON.parse(raw) as AuthSession;
    return { session, persistent };
  } catch (error) {
    console.error('[auth] Falha ao ler sessao salva:', error);
    return null;
  }
}

export function loadStoredSession(): StoredAuthSession | null {
  const local = safeParseSession(window.localStorage.getItem(LOCAL_STORAGE_KEY), true);
  if (local) return local;
  return safeParseSession(window.sessionStorage.getItem(SESSION_STORAGE_KEY), false);
}

export function saveStoredSession(session: AuthSession | null, persistent = false) {
  window.localStorage.removeItem(LOCAL_STORAGE_KEY);
  window.sessionStorage.removeItem(SESSION_STORAGE_KEY);

  if (!session) return;

  const target = persistent ? window.localStorage : window.sessionStorage;
  target.setItem(persistent ? LOCAL_STORAGE_KEY : SESSION_STORAGE_KEY, JSON.stringify(session));
}

// Helper: extrai endpoint logico da URL pra log (sem query string nem IDs).
// Ex: /api/v2/books/abc-uuid-123/sessions -> /api/v2/books/:id/sessions
function logEndpoint(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname
      .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:id')
      .replace(/\/\d{6,}/g, '/:id');
  } catch { return url.slice(0, 80); }
}

export async function apiFetchWithSession(
  session: AuthSession | null,
  input: string,
  init: RequestInit = {}
): Promise<Response> {
  const headers = new Headers(init.headers || {});
  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`);
  }
  const method = (init.method || 'GET').toUpperCase();
  const ep = logEndpoint(input);
  const t0 = performance.now();

  // Log de mutations (POST/PATCH/PUT/DELETE) — sao acoes do user, importantes
  if (method !== 'GET' && method !== 'HEAD') {
    tlog('api:req', { m: method, ep });
  }

  let res: Response;
  try {
    res = await fetch(input, { ...init, headers });
  } catch (e: any) {
    // Erro de rede (offline, DNS, etc) — sempre loga
    tlog('api:net_err', { m: method, ep, msg: e?.message?.slice(0, 200) });
    throw e;
  }

  const dt = Math.round(performance.now() - t0);

  // Loga sempre que !ok (4xx/5xx). Loga GETs lentos (>3s) ate quando ok.
  if (!res.ok) {
    tlog('api:err', { m: method, ep, status: res.status, dt });
  } else if (dt > 3000) {
    tlog('api:slow', { m: method, ep, status: res.status, dt });
  } else if (method !== 'GET' && method !== 'HEAD') {
    // Mutations bem-sucedidas — log compacto pra trilha de auditoria
    tlog('api:ok', { m: method, ep, dt });
  }
  // O teste grátis terminou (server/acesso-app.js): o App mostra o bloqueio.
  // Só 402 com { paywall: true } — os clubes também usam 402 (plan_required).
  if (res.status === 402) {
    res.clone().json().then((b) => {
      if (b && b.paywall === true) window.dispatchEvent(new CustomEvent('forjamente:paywall', { detail: b.acesso || null }));
    }).catch(() => {});
  }
  return res;
}

export async function refreshSession(refreshToken: string): Promise<AuthEnvelope> {
  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken })
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Sessão expirada');
  }

  return data as AuthEnvelope;
}
